import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { preserveNonDrawerParams } from "~/components/build/Show/lib/urlForView";
import { Job } from "~/components/build/Show/lib/types";
import { getDefaultJobTab, isTabAvailable } from "~/components/job/Job/jobTabs";

type UseJobTabSyncOptions = {
  job: Job | null | undefined;
  enabled?: boolean;
};

type UseJobTabSyncReturn = {
  activeTab: string | null | undefined;
  onTabChange: (tabId: string) => void;
};

/**
 * Derives the active job tab from URL parameters, falling back to the default
 * tab for the given job (annotations if available, otherwise output).
 */
export function useJobTabSync({ job, enabled = true }: UseJobTabSyncOptions): UseJobTabSyncReturn {
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();
  const params = useQueryParams();

  // Navigate to a tab in the URL, preserving non-drawer query params (e.g. annotations_tab)
  // and any hash fragment (e.g. log line references).
  const navigateToTab = useCallback(
    (tabId: string, { replace = false } = {}) => {
      const url = preserveNonDrawerParams(pathname, search, {
        jid: params.jid,
        sid: params.sid,
        tab: tabId,
      });
      navigate(`${url}${hash}`, { replace });
    },
    [navigate, pathname, search, hash, params.jid, params.sid],
  );

  useEffect(() => {
    if (!enabled || !job) {
      return;
    }

    const defaultTab = getDefaultJobTab(job);

    // If we have a tab in URL, validate it
    if (params.tab) {
      // If the tab is invalid for this job, redirect to the default tab
      if (!isTabAvailable(job, params.tab)) {
        navigateToTab(defaultTab, { replace: true });
      }
      return;
    }

    // No tab in URL — navigate to the default tab
    navigateToTab(defaultTab, { replace: true });
  }, [enabled, job, params.tab, params.jid, params.sid, navigateToTab]);

  // Update URL when tab changes
  const handleTabChange = useCallback(
    (tabId: string) => {
      navigateToTab(tabId);
    },
    [navigateToTab],
  );

  return {
    activeTab: params.tab ?? null,
    onTabChange: handleTabChange,
  };
}
